/**
 * Scripted opponents. They speak the same input a human would (look + buttons),
 * so they exercise the same code paths: wander, close, throw by range at a
 * zone (single strikes and short combos), cover the zone a loading limb goes
 * for, check low kicks, slip and switch stance.
 */
import { Buttons, emptyInput, type PlayerInput } from "./input.ts";
import { atan2, cos, sin, yawToRadians } from "./math/trig.ts";
import { distance, sub, unrotateY, type Vec3, v3 } from "./math/vec.ts";
import { createRng, nextFloat, nextInt, type Rng } from "./rng.ts";
import type { FighterState, Limb, MatchState, Zone } from "./state.ts";
import { yawToward } from "./step/movement.ts";
import { tuning } from "./tuning.ts";
import { aimButtons, coverButtons, zoneHeight } from "./zones.ts";

export interface BotBrain {
  rng: Rng;
  target: Vec3;
  retargetAt: number;
  guardUntil: number;
  /** Slip: the tick the lean is released, and which way it goes. */
  slipUntil: number;
  slipRight: boolean;
  /** Button held for a strike and the tick it is released. */
  holdButton: number;
  holdUntil: number;
  cooldownUntil: number;
  /** The zone this bot goes for: head, body or legs. */
  aimZone: Zone;
  reaimAt: number;
  /** The zone held while covering. */
  guardZone: Zone;
  /** Combo presses still to make (one tap each, oldest first) and the tick of the next. */
  combo: number[];
  comboAt: number;
  /** The stance this bot wants to fight in. */
  southpaw: boolean;
  /** Distance this bot likes to fight at: inside, punching or kicking range. */
  range: number;
}

export function createBotBrain(fighterId: number, seed: number): BotBrain {
  return {
    rng: createRng((seed ^ (fighterId * 2654435761)) >>> 0),
    target: v3(),
    retargetAt: 0,
    guardUntil: 0,
    slipUntil: 0,
    slipRight: false,
    holdButton: 0,
    holdUntil: 0,
    cooldownUntil: 0,
    aimZone: 0,
    reaimAt: 0,
    guardZone: 0,
    combo: [],
    comboAt: 0,
    southpaw: false,
    range: tuning.limbs.arm.reach + 0.15,
  };
}

const LIMB_BUTTON = [Buttons.ARM_L, Buttons.ARM_R, Buttons.LEG_L, Buttons.LEG_R] as const;

/** A strike by role: 0 lead arm, 1 rear arm, 2 lead leg, 3 rear leg; its arc bits; its zone. */
type Link = readonly [role: 0 | 1 | 2 | 3, arc: number, zone: Zone];

const H = Buttons.ARC_AROUND;
const U = Buttons.ARC_RISING;
/** Combos by range: what a fighter strings together inside, at punching and at kicking range. */
const COMBOS: Record<"inside" | "punch" | "kick", readonly (readonly Link[])[]> = {
  inside: [
    [
      [0, H, 0],
      [1, U, 0],
    ],
    [
      [1, H, 0],
      [0, H, 1],
    ],
    [
      [0, U, 0],
      [1, H, 0],
      [0, H, 1],
    ],
  ],
  punch: [
    [
      [0, 0, 0],
      [1, 0, 0],
    ],
    [
      [0, 0, 0],
      [1, 0, 0],
      [0, H, 0],
    ],
    [
      [0, 0, 0],
      [1, 0, 1],
    ],
    [
      [0, 0, 1],
      [1, 0, 0],
    ],
    [
      [0, 0, 0],
      [1, 0, 0],
      [3, H, 2],
    ],
  ],
  kick: [
    [
      [0, 0, 0],
      [3, H, 1],
    ],
    [
      [2, 0, 1],
      [3, H, 2],
    ],
  ],
};

/** The limb playing `role` in the bot's current stance. */
function limbOf(me: FighterState, role: 0 | 1 | 2 | 3): Limb {
  const orthodox = me.stance >= 0;
  if (role === 0) return orthodox ? 0 : 1;
  if (role === 1) return orthodox ? 1 : 0;
  if (role === 2) return orthodox ? 2 : 3;
  return orthodox ? 3 : 2;
}

/** Buttons for a strike by role: the limb, its arc and the modifier for its zone. */
function linkButtons(me: FighterState, link: Link): number {
  const limb = limbOf(me, link[0]);
  return LIMB_BUTTON[limb] | link[1] | aimButtons(limb, link[2]);
}

export function botInput(state: MatchState, me: FighterState, brain: BotBrain): PlayerInput {
  const input = emptyInput(state.tick);
  if (state.tick >= brain.retargetAt) {
    const r = state.cage.r * 0.5;
    brain.target = v3((nextFloat(brain.rng) * 2 - 1) * r, 0, (nextFloat(brain.rng) * 2 - 1) * r);
    brain.retargetAt = state.tick + 45 + nextInt(brain.rng, 60);
    if (nextFloat(brain.rng) < 0.3) {
      brain.guardUntil = state.tick + 30 + nextInt(brain.rng, 30);
      brain.guardZone = 0;
    }
    brain.range = 0.85 + nextFloat(brain.rng) * 0.55;
    // About one fighter in five is a southpaw (MMA: 17 %), and bots switch now and then.
    if (state.tick === 0 || nextFloat(brain.rng) < 0.08)
      brain.southpaw = nextFloat(brain.rng) < 0.2;
  }
  // Switch stance by pressing on alternate ticks until the feet agree.
  if (me.southpaw !== brain.southpaw && (me.prevButtons & Buttons.STANCE) === 0)
    input.buttons |= Buttons.STANCE;
  // Look at the nearest living opponent, or at the wander target.
  let nearest: FighterState | null = null;
  let best = Number.POSITIVE_INFINITY;
  for (const f of state.fighters) {
    if (f.id === me.id || !f.alive) continue;
    const d = distance(f.pos, me.pos);
    if (d < best) {
      best = d;
      nearest = f;
    }
  }
  input.yaw = nearest ? yawToward(me.pos, nearest.pos) : yawToward(me.pos, brain.target);

  // Fighting: close to striking range, pick a zone, throw by range, cover the zone coming.
  if (nearest && state.phase === "fight") {
    const range = brain.range;
    if (best > range) brain.target = nearest.pos;
    else brain.target = me.pos;
    if (state.tick >= brain.reaimAt) {
      const r = nextFloat(brain.rng);
      brain.aimZone = r < 0.4 ? 0 : r < 0.8 ? 1 : 2;
      brain.reaimAt = state.tick + 20 + nextInt(brain.rng, 40);
    }
    // The look only picks the opponent now; the head still turns to where it hits.
    const eyeY = tuning.body.headY - tuning.movement.duckDrop * me.duck;
    input.pitch = Math.round(
      (atan2(zoneHeight(brain.aimZone) - eyeY, Math.max(0.3, best)) * 180) / Math.PI,
    );
    const incoming = nearest.chargeArm ?? nearest.chargeLeg;
    if (brain.combo.length > 0) {
      // One tap per press, a tick apart, so each press is its own edge.
      if (state.tick >= brain.comboAt) {
        input.buttons |= brain.combo.shift() ?? 0;
        brain.comboAt = state.tick + 2;
        if (brain.combo.length === 0)
          brain.cooldownUntil = state.tick + 20 + nextInt(brain.rng, 15);
      }
    } else if (brain.holdButton !== 0) {
      if (state.tick < brain.holdUntil) input.buttons |= brain.holdButton;
      else {
        brain.holdButton = 0;
        brain.cooldownUntil = state.tick + 10 + nextInt(brain.rng, 15);
      }
    } else if (best <= range + 0.3 && state.tick >= brain.cooldownUntil && !me.exhausted) {
      const r = nextFloat(brain.rng);
      if (incoming && r < 0.35) {
        // Cover the zone a loading limb goes for (a human reads the wind-up).
        brain.guardUntil = state.tick + 8 + nextInt(brain.rng, 8);
        brain.guardZone = incoming.zone;
      } else if (incoming && r < 0.5) {
        brain.slipUntil = state.tick + 6 + nextInt(brain.rng, 8);
        brain.slipRight = nextFloat(brain.rng) < 0.5;
      } else if (r < 0.04 && state.tick >= brain.guardUntil) {
        const set = best < 0.9 ? COMBOS.inside : best < 1.2 ? COMBOS.punch : COMBOS.kick;
        const combo = set[nextInt(brain.rng, set.length)] ?? [];
        brain.combo = combo.map((link) => linkButtons(me, link));
        brain.comboAt = state.tick;
      } else if (r < 0.12) {
        const link = pickStrike(brain, best);
        brain.holdButton = linkButtons(me, [link[0], link[1], brain.aimZone]);
        brain.holdUntil = state.tick + 3 + nextInt(brain.rng, 10);
        input.buttons |= brain.holdButton;
      }
    }
  }

  // Walk toward the target, expressed relative to the look yaw.
  const to = sub(brain.target, me.pos);
  if (distance(brain.target, me.pos) > 0.5) {
    const a = yawToRadians(input.yaw);
    // Same change of basis the movement code uses; never inline it again.
    const local = unrotateY(to, sin(a), cos(a));
    const mag = Math.sqrt(local.x * local.x + local.z * local.z) || 1;
    input.moveX = Math.round((local.x / mag) * 127);
    input.moveY = Math.round((local.z / mag) * 127);
  }
  if (state.tick < brain.guardUntil) input.buttons |= Buttons.GUARD | coverButtons(brain.guardZone);
  if (state.tick < brain.slipUntil)
    input.buttons |= brain.slipRight ? Buttons.LEAN_R : Buttons.LEAN_L;
  return input;
}

/**
 * A strike for the range, by role and arc: inside, hooks and uppercuts (a
 * straight jams); at punching range, the jab and the cross; further out, kicks.
 */
function pickStrike(brain: BotBrain, distance: number): readonly [0 | 1 | 2 | 3, number] {
  const r = nextFloat(brain.rng);
  const side: 0 | 1 = nextFloat(brain.rng) < 0.5 ? 0 : 1;
  if (distance < 0.9) return r < 0.45 ? [side, H] : r < 0.8 ? [side, U] : [1, 0];
  if (distance < 1.2) return r < 0.4 ? [0, 0] : r < 0.7 ? [1, 0] : r < 0.85 ? [side, H] : [2, 0];
  return r < 0.45 ? [3, H] : r < 0.7 ? [2, 0] : r < 0.85 ? [3, 0] : [0, 0];
}
